import styled from '@emotion/styled';
import { motion } from 'framer-motion';

const PageWrapper = styled.div`
  width: 100%;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem 2rem;

  @media (max-width: 768px) {
    padding: 0 1rem 1rem;
  }
`;

const HeroWrapper = styled.div`
  width: 100%;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const Hero = styled.div<{ $bgImage: string }>`
  text-align: center;
  padding: 8rem 2rem;
  position: relative;
  overflow: hidden;
  min-height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => `url(${props.$bgImage})`} center/cover no-repeat;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to bottom,
      rgba(15, 15, 35, 0.5) 0%,
      rgba(15, 15, 35, 0.7) 50%,
      rgba(15, 15, 35, 0.95) 100%
    );
  }

  @media (max-width: 768px) {
    padding: 5rem 1.5rem;
    min-height: 400px;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
`;

const Title = styled(motion.h1)`
  font-size: 3.5rem;
  color: white;
  margin-bottom: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.4rem;
  color: rgba(255, 255, 255, 0.95);
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.7;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const Section = styled.section`
  margin: 6rem 0;

  @media (max-width: 768px) {
    margin: 4rem 0;
  }
`;

const ProblemSection = styled(Section)`
  background: #f7fafc;
  margin-left: -2rem;
  margin-right: -2rem;
  padding: 4rem 2rem;

  @media (max-width: 768px) {
    margin-left: -1rem;
    margin-right: -1rem;
    padding: 3rem 1.5rem;
  }
`;

const ProblemContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const ProblemQuote = styled(motion.blockquote)`
  font-size: 1.3rem;
  line-height: 1.9;
  color: #2d3748;
  border-left: 4px solid #667eea;
  padding-left: 2rem;
  margin: 0;
  font-style: italic;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    padding-left: 1.5rem;
  }
`;

const BeliefExamples = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  margin: 2rem 0;
`;

const BeliefTag = styled(motion.span)`
  background: #1a1a2e;
  border: 1px solid #48bb78;
  color: #48bb78;
  padding: 0.6rem 1.2rem;
  border-radius: 2rem;
  font-size: 0.95rem;
  font-weight: 500;
  font-style: italic;
`;

const Highlight = styled.span`
  color: #48bb78;
  font-weight: 600;
  font-style: normal;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.2rem;
  color: #2d3748;
  margin-bottom: 1rem;
  text-align: center;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  font-size: 1.1rem;
  color: #718096;
  text-align: center;
  max-width: 600px;
  margin: 0 auto 3rem;
  line-height: 1.6;
`;

const PillarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const PillarCard = styled(motion.div)`
  background: #1a1a2e;
  border-radius: 1.5rem;
  padding: 2.5rem 2rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  border: 1px solid #2a2a4a;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #48bb78 0%, #38a169 100%);
  }
`;

const PillarNumber = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
`;

const PillarTitle = styled.h3`
  font-size: 1.4rem;
  color: #fff;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const PillarDescription = styled.p`
  color: #a0aec0;
  line-height: 1.7;
  font-size: 1rem;
`;

const FlowchartSection = styled(Section)`
  text-align: center;
`;

const FlowchartPlaceholder = styled(motion.div)`
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
  border: 2px dashed #cbd5e0;
  border-radius: 1.5rem;
  padding: 4rem 2rem;
  margin-top: 2rem;
  color: #718096;
  font-size: 1.1rem;
`;

const FlowchartImage = styled(motion.img)`
  max-width: 100%;
  border-radius: 1.5rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  margin-top: 2rem;
`;

const AppShowcase = styled(Section)`
  background: linear-gradient(135deg, #1a202c 0%, #2d3748 100%);
  margin-left: -2rem;
  margin-right: -2rem;
  padding: 5rem 2rem;
  text-align: center;

  @media (max-width: 768px) {
    margin-left: -1rem;
    margin-right: -1rem;
    padding: 3rem 1.5rem;
  }
`;

const AppShowcaseTitle = styled(motion.h2)`
  font-size: 2.2rem;
  color: white;
  margin-bottom: 1rem;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const AppShowcaseSubtitle = styled(motion.p)`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.8);
  max-width: 600px;
  margin: 0 auto 3rem;
  line-height: 1.6;
`;

const AppScreenshotPlaceholder = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  border: 2px dashed rgba(255, 255, 255, 0.3);
  border-radius: 1.5rem;
  padding: 6rem 2rem;
  max-width: 600px;
  margin: 0 auto;
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.1rem;
`;

const AppScreenshot = styled(motion.img)`
  max-width: 100%;
  border-radius: 1rem;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.4);
  background: white;
  padding: 1rem;

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const ResearchSection = styled(Section)`
  text-align: center;
`;

const ResearchLogos = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 3rem;
  justify-content: center;
  align-items: center;
  margin-top: 2rem;
  opacity: 0.7;
`;

const ResearchLogo = styled(motion.img)`
  max-width: 140px;
  height: auto;
  opacity: 0.7;
  filter: grayscale(100%);
  transition: all 0.3s ease;

  &:hover {
    opacity: 1;
    filter: grayscale(0%);
  }

  @media (max-width: 768px) {
    max-width: 100px;
  }
`;

const CTASection = styled(Section)`
  text-align: center;
  padding: 4rem 2rem;
  background: #0f0f23;
  border: 1px solid #2a2a4a;
  border-radius: 2rem;
  margin-bottom: 4rem;
`;

const CTATitle = styled(motion.h2)`
  font-size: 2rem;
  color: white;
  margin-bottom: 1rem;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const CTAText = styled(motion.p)`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 500px;
  margin: 0 auto 2rem;
  line-height: 1.6;
`;

const CTAButton = styled(motion.a)`
  display: inline-block;
  background: #48bb78;
  color: white;
  padding: 1rem 2.5rem;
  border-radius: 3rem;
  font-size: 1.1rem;
  font-weight: 600;
  text-decoration: none;
  box-shadow: 0 10px 30px rgba(72, 187, 120, 0.3);
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
    background: #38a169;
  }
`;

// Team Section Styles
const TeamSection = styled(Section)`
  background: #0f0f23;
  margin-left: -2rem;
  margin-right: -2rem;
  padding: 5rem 2rem;

  @media (max-width: 768px) {
    margin-left: -1rem;
    margin-right: -1rem;
    padding: 3rem 1.5rem;
  }
`;

const TeamContent = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const TeamImage = styled(motion.img)`
  width: 100%;
  border-radius: 1rem;
  margin-bottom: 3rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const TeamMember = styled(motion.div)`
  text-align: center;
`;

const TeamMemberName = styled.h3`
  font-size: 1.3rem;
  color: #fff;
  margin-bottom: 0.3rem;
  font-weight: 600;
`;

const TeamMemberRole = styled.p`
  color: #48bb78;
  font-size: 0.95rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const TeamMemberBio = styled.p`
  color: #a0aec0;
  font-size: 0.9rem;
  line-height: 1.5;
`;

// Configuration flags for placeholder sections
// Set to true once you have the actual images
const HAS_FLOWCHART_IMAGE = true;
const HAS_APP_SCREENSHOT = true;
const HAS_RESEARCH_LOGOS = true;


const partnerLogos = [
  { src: 'washington.png', alt: 'University of Washington' },
  { src: 'meta.png', alt: 'Meta' },
  { src: 'deloitte.png', alt: 'Deloitte' },
  { src: 'padova.png', alt: 'University of Padova' },
  { src: 'valencia.png', alt: 'University of Valencia' },
  { src: 'reichman.png', alt: 'Reichman University' },
];

interface AboutProps {
  onDemoOpen: () => void;
}

const About = ({ onDemoOpen }: AboutProps) => {
  const themeUrl = window.wpData?.themeUrl || '';

  const beliefExamples = [
    "I'm unlovable",
    "If I'm not perfect, I'm worthless",
    "The world is dangerous",
    "I can't cope",
    "People will abandon me",
  ];

  return (
    <PageWrapper>
      {/* Hero Section - Full Bleed */}
      <HeroWrapper>
        <Hero $bgImage={`${themeUrl}/images/about-hero.jpg`}>
          <HeroContent>
          <Title
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Mapping the Mind's Hidden Traps
          </Title>
          <Subtitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            We decode the maladaptive beliefs that keep people stuck—then build
            game-like experiences to rewire them.
          </Subtitle>
          </HeroContent>
        </Hero>
      </HeroWrapper>

      <Container>
      {/* The Problem Section */}
      <ProblemSection>
        <ProblemContent>
          <ProblemQuote
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Every mental health condition has a fingerprint—a constellation of
            deeply held beliefs that fuel suffering.
          </ProblemQuote>

          <BeliefExamples>
            {beliefExamples.map((belief, index) => (
              <BeliefTag
                key={belief}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                "{belief}"
              </BeliefTag>
            ))}
          </BeliefExamples>

          <ProblemQuote
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            These cognitive traps run silently in the background, shaping thoughts,
            emotions, and behaviors. Traditional therapy can take months to uncover them.
            <Highlight> We accelerate that process.</Highlight>
          </ProblemQuote>
        </ProblemContent>
      </ProblemSection>

      {/* Our Approach Section */}
      <Section>
        <SectionTitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Our Approach
        </SectionTitle>
        <SectionSubtitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          Three pillars that transform how we understand and treat mental health
        </SectionSubtitle>

        <PillarGrid>
          <PillarCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <PillarNumber>1</PillarNumber>
            <PillarTitle>Map the Belief Architecture</PillarTitle>
            <PillarDescription>
              We've built a comprehensive taxonomy linking conditions—anxiety,
              depression, OCD, trauma—to the specific maladaptive beliefs that
              drive them. This isn't guesswork. It's structured CBT science
              made programmable.
            </PillarDescription>
          </PillarCard>

          <PillarCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <PillarNumber>2</PillarNumber>
            <PillarTitle>Target with Precision</PillarTitle>
            <PillarDescription>
              Once we identify the beliefs keeping someone stuck, we deploy
              targeted micro-interventions. Game-like, engaging, designed to
              bypass resistance and create real cognitive shifts.
            </PillarDescription>
          </PillarCard>

          <PillarCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <PillarNumber>3</PillarNumber>
            <PillarTitle>Validate Through Research</PillarTitle>
            <PillarDescription>
              Every intervention we build goes through clinical validation.
              We partner with researchers and healthcare systems to prove
              what works—and iterate relentlessly on what doesn't.
            </PillarDescription>
          </PillarCard>
        </PillarGrid>
      </Section>

      {/* Belief Mapping Flowchart Section */}
      <FlowchartSection>
        <SectionTitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          The Belief Fingerprint
        </SectionTitle>
        <SectionSubtitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          How we map conditions to their underlying cognitive patterns
        </SectionSubtitle>

        {HAS_FLOWCHART_IMAGE ? (
          <FlowchartImage
            src={`${themeUrl}/images/belief-mapping-flowchart.jpeg`}
            alt="CBT Interactive System Architecture: Conditions mapped to cognitive patterns and targeted interventions"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          />
        ) : (
          <FlowchartPlaceholder
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            [Flowchart: Condition → Core Beliefs → Cognitive Distortions → Behaviors]
            <br /><br />
            <em>Add your belief mapping diagram here</em>
          </FlowchartPlaceholder>
        )}
      </FlowchartSection>

      {/* App Showcase Section */}
      <AppShowcase>
        <AppShowcaseTitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Game-Like Interventions That Work
        </AppShowcaseTitle>
        <AppShowcaseSubtitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          Engaging experiences that feel like play but deliver therapeutic impact.
          Designed to bypass cognitive resistance and create lasting change.
        </AppShowcaseSubtitle>

        {HAS_APP_SCREENSHOT ? (
          <AppScreenshot
            src={`${themeUrl}/images/app-screens.png`}
            alt="GGtude app: Assessments & Tracking, Psycho-education, Game-like exercises, Personal track creation"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          />
        ) : (
          <AppScreenshotPlaceholder
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            [App Screenshot]
            <br /><br />
            <em>Add your game-like intervention screenshot here</em>
          </AppScreenshotPlaceholder>
        )}
      </AppShowcase>

      {/* Research & Validation Section */}
      {HAS_RESEARCH_LOGOS && (
        <ResearchSection>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Research-Driven Innovation
          </SectionTitle>
          <SectionSubtitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Validated in partnership with leading institutions
          </SectionSubtitle>

          <ResearchLogos>
            {partnerLogos.map((logo, index) => (
              <ResearchLogo
                key={logo.src}
                src={`${themeUrl}/images/logos/${logo.src}`}
                alt={logo.alt}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 0.7, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              />
            ))}
          </ResearchLogos>
        </ResearchSection>
      )}

      {/* Team Section */}
      <TeamSection>
        <TeamContent>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ color: '#fff' }}
          >
            The Team
          </SectionTitle>
          <SectionSubtitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            style={{ color: '#a0aec0' }}
          >
            Research-led innovation, built to scale
          </SectionSubtitle>

          <TeamImage
            src={`${themeUrl}/images/team.jpg`}
            alt="GGtude founding team"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          />

          <TeamGrid>
            <TeamMember
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <TeamMemberName>Gur Ilany</TeamMemberName>
              <TeamMemberRole>CEO & Product Lead</TeamMemberRole>
              <TeamMemberBio>
                Driving product innovation and company vision
              </TeamMemberBio>
            </TeamMember>

            <TeamMember
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <TeamMemberName>Prof. Guy Doron</TeamMemberName>
              <TeamMemberRole>Lead of Research</TeamMemberRole>
              <TeamMemberBio>
                Leading the science behind our CBT-based interventions
              </TeamMemberBio>
            </TeamMember>

            <TeamMember
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <TeamMemberName>Guy Bino</TeamMemberName>
              <TeamMemberRole>COO & BizDev</TeamMemberRole>
              <TeamMemberBio>
                Scaling operations and strategic partnerships
              </TeamMemberBio>
            </TeamMember>
          </TeamGrid>
        </TeamContent>
      </TeamSection>

      {/* CTA Section */}
      <CTASection>
        <CTATitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Ready to Transform Mental Health Care?
        </CTATitle>
        <CTAText
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          Join the healthcare providers and organizations using GGtude to
          deliver better outcomes.
        </CTAText>
        <CTAButton
          href="#"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            onDemoOpen();
          }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
        >
          Get in Touch
        </CTAButton>
      </CTASection>
      </Container>
    </PageWrapper>
  );
};

export default About; 